import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  Box,
  Divider,
  Chip,
  CircularProgress,
  Stack,
  Tabs,
  Tab
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationImportant as ReminderIcon,
  EventAvailable as ConfirmationIcon,
  EventBusy as CancellationIcon,
  TaskAlt as CompletedIcon,
  Schedule as AppointmentReminderIcon,
  Update as RescheduleIcon,
  AccessTime as RemindersTabIcon,
  Event as EventsTabIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { apiService } from '../../services/api';

const NOTIFICATION_TYPES = {
  mot_reminder: {
    name: 'MOT Expiry Reminders',
    description: 'Send SMS reminders before MOT expires',
    icon: <ReminderIcon />,
    color: 'warning',
    category: 'reminders'
  },
  appointment_reminder: {
    name: 'Appointment Reminders',
    description: 'Send SMS reminders before appointment date',
    icon: <AppointmentReminderIcon />,
    color: 'info',
    category: 'reminders'
  },
  booking_confirmation: {
    name: 'Booking Confirmations',
    description: 'Send SMS when booking is confirmed',
    icon: <ConfirmationIcon />,
    color: 'success',
    category: 'events'
  },
  booking_cancellation: {
    name: 'Booking Cancellations',
    description: 'Send SMS when booking is cancelled',
    icon: <CancellationIcon />,
    color: 'error',
    category: 'events'
  },
  booking_completed: {
    name: 'Service Completed',
    description: 'Send SMS when service is completed',
    icon: <CompletedIcon />,
    color: 'primary',
    category: 'events'
  },
  booking_reschedule: {
    name: 'Booking Rescheduled',
    description: 'Send SMS when booking date/time is changed',
    icon: <RescheduleIcon />,
    color: 'info',
    category: 'events'
  }
};

const NotificationSettings = ({ garageId, configs = [], onConfigChange }) => {
  const theme = useTheme();
  
  // Configuration state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 = reminders, 1 = events
  

  useEffect(() => {
    setLoading(false);
  }, [configs]);

  const updateConfig = (notificationType, field, value) => {
    const updatedConfigs = configs.map(config => 
      config.notification_type === notificationType
        ? {
            ...config,
            [field]: field === 'is_enabled' ? value : config.is_enabled,
            settings: field !== 'is_enabled' 
              ? { ...config.settings, [field]: value }
              : config.settings
          }
        : config
    );
    onConfigChange(updatedConfigs);
  };

  // Filter configs by category
  const getConfigsByCategory = (category) => {
    return configs.filter(config => {
      const typeInfo = NOTIFICATION_TYPES[config.notification_type];
      return typeInfo && typeInfo.category === category;
    });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };


  const renderNotificationTypeConfig = (config) => {
    const typeInfo = NOTIFICATION_TYPES[config.notification_type];
    if (!typeInfo) return null;

    return (
      <Card 
        key={config.notification_type} 
        variant="outlined" 
        sx={{ 
          mb: 2,
          backgroundColor: config.is_enabled 
            ? theme.palette.mode === 'dark' ? 'primary.dark' : 'primary.50'
            : theme.palette.background.paper,
          borderColor: config.is_enabled 
            ? theme.palette.primary.main 
            : theme.palette.divider,
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            borderColor: theme.palette.primary.main,
            boxShadow: theme.shadows[2]
          }
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            gap: 2, 
            mb: (config.notification_type === 'mot_reminder' || config.notification_type === 'appointment_reminder') && config.is_enabled ? 3 : 0
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              flex: 1,
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Box 
                sx={{ 
                  color: `${typeInfo.color}.main`,
                  p: 1,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? `${typeInfo.color}.dark` 
                    : `${typeInfo.color}.50`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {typeInfo.icon}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    mb: 0.5
                  }}
                >
                  {typeInfo.name}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    lineHeight: 1.4
                  }}
                >
                  {typeInfo.description}
                </Typography>
              </Box>
            </Box>
            
            {/* Toggle Section */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              flexShrink: 0,
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'space-between', sm: 'flex-end' }
            }}>
              <Chip
                label={config.is_enabled ? 'Enabled' : 'Disabled'}
                color={config.is_enabled ? 'success' : 'default'}
                size="small"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.75rem'
                }}
              />
              <Switch
                checked={config.is_enabled}
                onChange={(e) => updateConfig(config.notification_type, 'is_enabled', e.target.checked)}
                color="primary"
                size="medium"
              />
            </Box>
          </Box>

          {/* MOT Reminder Days Configuration */}
          {config.notification_type === 'mot_reminder' && config.is_enabled && (
            <Box sx={{ 
              mt: 2,
              pt: 2,
              borderTop: 1,
              borderColor: 'divider'
            }}>
              <TextField
                label="Days Before Expiry"
                type="number"
                value={config.settings?.reminder_days_before || 30}
                onChange={(e) => updateConfig(config.notification_type, 'reminder_days_before', parseInt(e.target.value) || 0)}
                inputProps={{ min: 1, max: 365 }}
                helperText="Number of days before MOT expiry to send the reminder"
                size="small"
                sx={{ 
                  maxWidth: { xs: '100%', sm: 250 },
                  '& .MuiInputBase-root': {
                    fontSize: '0.875rem'
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '0.75rem'
                  }
                }}
              />
            </Box>
          )}

          {/* Appointment Reminder Days Configuration */}
          {config.notification_type === 'appointment_reminder' && config.is_enabled && (
            <Box sx={{ 
              mt: 2,
              pt: 2,
              borderTop: 1,
              borderColor: 'divider'
            }}>
              <TextField
                label="Days Before Appointment"
                type="number"
                value={config.settings?.days_before || 1}
                onChange={(e) => updateConfig(config.notification_type, 'days_before', parseInt(e.target.value) || 0)}
                inputProps={{ min: 1, max: 7 }}
                helperText="Number of days before appointment to send the reminder (sent daily at 9 AM)"
                size="small"
                sx={{ 
                  maxWidth: { xs: '100%', sm: 250 },
                  '& .MuiInputBase-root': {
                    fontSize: '0.875rem'
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '0.75rem'
                  }
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const reminderConfigs = getConfigsByCategory('reminders');
  const eventConfigs = getConfigsByCategory('events');
  const currentConfigs = activeTab === 0 ? reminderConfigs : eventConfigs;
  const currentCategory = activeTab === 0 ? 'reminders' : 'events';

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 3,
            fontSize: { xs: '1.125rem', sm: '1.25rem' },
            fontWeight: 600,
            color: theme.palette.text.primary
          }}
        >
          Notification Settings
        </Typography>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              '& .MuiAlert-message': {
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }
            }}
          >
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }
            }}
          >
            <Tab 
              icon={<RemindersTabIcon />} 
              label={`Reminders (${reminderConfigs.length})`}
              sx={{ color: 'text.primary' }}
            />
            <Tab 
              icon={<EventsTabIcon />} 
              label={`Events (${eventConfigs.length})`}
              sx={{ color: 'text.primary' }}
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Stack spacing={{ xs: 2, sm: 2 }}>
          {currentConfigs.map(config => renderNotificationTypeConfig(config))}
        </Stack>

        {currentConfigs.length === 0 && !loading && (
          <Box sx={{ 
            textAlign: 'center', 
            py: { xs: 4, sm: 6 },
            color: 'text.secondary'
          }}>
            {activeTab === 0 ? 
              <RemindersTabIcon sx={{ fontSize: { xs: 48, sm: 64 }, mb: 2, opacity: 0.5 }} /> : 
              <EventsTabIcon sx={{ fontSize: { xs: 48, sm: 64 }, mb: 2, opacity: 0.5 }} />
            }
            <Typography variant="h6" sx={{ mb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              No {currentCategory} settings found
            </Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              {currentCategory === 'reminders' 
                ? 'Reminder notification settings will appear here once configured'
                : 'Event notification settings will appear here once configured'
              }
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NotificationSettings;